// Value references: the strings auto-fill, writeback, start-new and link attribute
// mappings store, e.g. "{task_3_value:code}" or "{task_2_attr:manager}". The parser
// mirrors parseValueReference in internal/workflow/types/startnew_resolver.go; the
// shared fixture test/fixtures/value_refs.json keeps the two in step.
//
// No "@/" imports: the Playwright unit project loads this file directly.

export type TaskValuePart = 'value' | 'code' | 'label' | 'attr';

export type ValueRef =
  | { kind: 'task'; row: number; part: TaskValuePart; field?: string }
  | { kind: 'launch'; key: string }
  | { kind: 'now' }
  | { kind: 'source_key' }
  // Anything else is saved as typed: plain text, or a braced reference with an error.
  | { kind: 'text'; text: string; error?: string };

// A reference to a task that was removed (deleted, or its name cleared) holds "?" and the
// task's id in place of its row number, e.g. {task_?cr1abc_value}. The backend can't read
// it, and the picker flags it; if the task gets its name back the number is restored.
const REMOVED_MARKER = '?';

/** Parses a stored value. Never throws: an unreadable reference comes back as text with an error. */
export function parseValueRef(raw: string): ValueRef {
  if (!raw.startsWith('{') || !raw.endsWith('}')) return { kind: 'text', text: raw };
  const inner = raw.slice(1, -1);
  const invalid = (error: string): ValueRef => ({ kind: 'text', text: raw, error });

  if (inner === 'now') return { kind: 'now' };
  if (inner === 'source_key') return { kind: 'source_key' };

  if (inner.startsWith('attr_')) {
    const key = inner.slice('attr_'.length);
    return key ? { kind: 'launch', key } : invalid('The launch value is missing its row number.');
  }

  if (!inner.startsWith('task_')) return invalid('This is not a known reference.');
  const rest = inner.slice('task_'.length);
  const cut = rest.indexOf('_');
  if (cut < 0) return invalid('Add _value or _attr:field after the task number.');
  const rowText = rest.slice(0, cut);
  const selector = rest.slice(cut + 1);
  if (rowText.startsWith(REMOVED_MARKER)) return invalid('The task this read was deleted or lost its name. Choose another value.');
  // strconv.Atoi accepts a sign, so the backend reads "+3" as row 3.
  if (!/^[+-]?\d+$/.test(rowText)) return invalid(`"${rowText}" is not a task number.`);
  const row = Number(rowText);

  if (selector.startsWith('attr:')) {
    const field = selector.slice('attr:'.length);
    return field ? { kind: 'task', row, part: 'attr', field } : invalid('Add a field name after attr:.');
  }

  const colon = selector.indexOf(':');
  const name = colon < 0 ? selector : selector.slice(0, colon);
  if (name !== 'value') return invalid(`"${name}" is not a part of an answer. Use value or attr:field.`);
  if (colon < 0) return { kind: 'task', row, part: 'value' };
  const modifier = selector.slice(colon + 1);
  if (modifier !== 'code' && modifier !== 'label') return invalid(`"${modifier}" is not a part of an answer. Use code or label.`);
  return { kind: 'task', row, part: modifier };
}

export function formatValueRef(ref: ValueRef): string {
  switch (ref.kind) {
    case 'task':
      if (ref.part === 'attr') return `{task_${ref.row}_attr:${ref.field ?? ''}}`;
      if (ref.part === 'value') return `{task_${ref.row}_value}`;
      return `{task_${ref.row}_value:${ref.part}}`;
    case 'launch':
      return `{attr_${ref.key}}`;
    case 'now':
      return '{now}';
    case 'source_key':
      return '{source_key}';
    case 'text':
      return ref.text;
  }
}

// ─── Renumbering ──────────────────────────────────────────────────────────────

type TaskRow = { id: string; name: string };

/** Row numbers by task id: a task's row is its position among named rows, as saved. */
export function rowNumbersById(rows: TaskRow[]): Map<string, number> {
  const numbers = new Map<string, number>();
  rows.filter((row) => row.name.trim()).forEach((row, index) => numbers.set(row.id, index + 1));
  return numbers;
}

/** How an edit to the grid moved tasks: which task each old row number held, and each task's new row. */
export interface Renumbering {
  idsByOldRow: Map<number, string>;
  newRowsById: Map<string, number>;
}

/** Null when no task changed its number and none was added. */
export function buildRenumbering(before: TaskRow[], after: TaskRow[]): Renumbering | null {
  const oldRows = rowNumbersById(before);
  const newRowsById = rowNumbersById(after);
  let changed = false;
  oldRows.forEach((row, id) => {
    if (newRowsById.get(id) !== row) changed = true;
  });
  newRowsById.forEach((_, id) => {
    if (!oldRows.has(id)) changed = true;
  });
  if (!changed) return null;
  const idsByOldRow = new Map<number, string>();
  oldRows.forEach((row, id) => idsByOldRow.set(row, id));
  return { idsByOldRow, newRowsById };
}

/**
 * The new spelling of a row number held in a string: a number, the removed marker, or
 * undefined to leave it as it is (not a row number, or that task kept its row).
 */
function renumberRowText(rowText: string, renumbering: Renumbering): string | undefined {
  if (rowText.startsWith(REMOVED_MARKER)) {
    const newRow = renumbering.newRowsById.get(rowText.slice(REMOVED_MARKER.length));
    return newRow === undefined ? undefined : String(newRow);
  }
  if (!/^[+-]?\d+$/.test(rowText)) return undefined;
  const oldRow = Number(rowText);
  const id = renumbering.idsByOldRow.get(oldRow);
  if (id === undefined) return undefined;
  const newRow = renumbering.newRowsById.get(id);
  if (newRow === oldRow) return undefined;
  return newRow === undefined ? REMOVED_MARKER + id : String(newRow);
}

/** Points a task reference at its task's new row. Every other value comes back unchanged. */
export function renumberValueRef(raw: string, renumbering: Renumbering): string {
  const match = /^\{task_([^_]+)_/.exec(raw);
  if (!match || !raw.endsWith('}')) return raw;
  const newRowText = renumberRowText(match[1], renumbering);
  // Swap only the number, so the rest of the string stays byte-for-byte as stored.
  return newRowText === undefined ? raw : `{task_${newRowText}_${raw.slice(match[0].length)}`;
}

function renumberValues(mapping: Record<string, string>, renumbering: Renumbering): Record<string, string> {
  return Object.fromEntries(Object.entries(mapping).map(([key, value]) => [key, renumberValueRef(value, renumbering)]));
}

/** A row number held as a number; a removed task clears it rather than leave it on another task. */
function renumberRow(row: number | undefined, renumbering: Renumbering): number | undefined {
  if (row === undefined) return row;
  const id = renumbering.idsByOldRow.get(row);
  return id === undefined ? row : renumbering.newRowsById.get(id);
}

/** The task config fields that refer to other tasks by row. */
export interface ValueRefConfig {
  /** Keys are the rows to fill; values are references. */
  autoFillConfig?: Record<string, string>;
  writebackConfigs?: { keyRef?: string; labelRef?: string; parentKeyRef?: string; fields?: Record<string, string> }[];
  /** Mapping keys belong to the target workflow; only the values are references. */
  startnewConfig?: { targetTask?: number; attributeMapping?: Record<string, string> };
  validationConfig?: { reference_task?: number };
  dataSourceConfig?: { locationTask?: number; regionTask?: number; filterTask?: number };
  skipConfig?: { conditions?: { conditionTask?: number; conditionValueTask?: number }[] };
}

/**
 * Renumbers every task reference in a task's config. An auto-fill target that was removed
 * keeps its entry under the removed marker, which the backend skips like any key that
 * isn't a row number; keys that aren't row numbers (tag keys) are kept as they are.
 */
export function renumberConfigRefs<T extends ValueRefConfig>(config: T, renumbering: Renumbering): T {
  const next = { ...config };
  if (config.autoFillConfig) {
    next.autoFillConfig = Object.fromEntries(
      Object.entries(config.autoFillConfig).map(([target, value]) => [
        renumberRowText(target, renumbering) ?? target,
        renumberValueRef(value, renumbering),
      ]),
    );
  }
  if (config.writebackConfigs) {
    next.writebackConfigs = config.writebackConfigs.map((policy) => ({
      ...policy,
      ...(policy.keyRef !== undefined && { keyRef: renumberValueRef(policy.keyRef, renumbering) }),
      ...(policy.labelRef !== undefined && { labelRef: renumberValueRef(policy.labelRef, renumbering) }),
      ...(policy.parentKeyRef !== undefined && { parentKeyRef: renumberValueRef(policy.parentKeyRef, renumbering) }),
      ...(policy.fields !== undefined && { fields: renumberValues(policy.fields, renumbering) }),
    }));
  }
  if (config.startnewConfig) {
    const { targetTask, attributeMapping } = config.startnewConfig;
    next.startnewConfig = {
      ...config.startnewConfig,
      ...(targetTask !== undefined && { targetTask: renumberRow(targetTask, renumbering) }),
      ...(attributeMapping !== undefined && { attributeMapping: renumberValues(attributeMapping, renumbering) }),
    };
  }
  if (config.validationConfig?.reference_task !== undefined) {
    next.validationConfig = { ...config.validationConfig, reference_task: renumberRow(config.validationConfig.reference_task, renumbering) };
  }
  if (config.dataSourceConfig) {
    const { locationTask, regionTask, filterTask } = config.dataSourceConfig;
    next.dataSourceConfig = {
      ...config.dataSourceConfig,
      ...(locationTask !== undefined && { locationTask: renumberRow(locationTask, renumbering) }),
      ...(regionTask !== undefined && { regionTask: renumberRow(regionTask, renumbering) }),
      ...(filterTask !== undefined && { filterTask: renumberRow(filterTask, renumbering) }),
    };
  }
  if (config.skipConfig?.conditions) {
    next.skipConfig = {
      ...config.skipConfig,
      conditions: config.skipConfig.conditions.map((condition) => ({
        ...condition,
        ...(condition.conditionTask !== undefined && { conditionTask: renumberRow(condition.conditionTask, renumbering) }),
        ...(condition.conditionValueTask !== undefined && { conditionValueTask: renumberRow(condition.conditionValueTask, renumbering) }),
      })),
    };
  }
  return next;
}

/** After rows are moved, removed, duplicated, named or un-named, renumbers every row's references to follow their tasks. */
export function renumberRows<T extends TaskRow & ValueRefConfig>(before: T[], after: T[]): T[] {
  const renumbering = buildRenumbering(before, after);
  if (!renumbering) return after;
  return after.map((row) => renumberConfigRefs(row, renumbering));
}

/** Whether a config still reads a removed task, which must be fixed before saving. */
export function readsRemovedTask(config: ValueRefConfig): boolean {
  const marker = `{task_${REMOVED_MARKER}`;
  const refs = [
    ...Object.keys(config.autoFillConfig ?? {}).map((key) => (key.startsWith(REMOVED_MARKER) ? marker : '')),
    ...Object.values(config.autoFillConfig ?? {}),
    ...(config.writebackConfigs ?? []).flatMap((policy) => [policy.keyRef, policy.labelRef, policy.parentKeyRef, ...Object.values(policy.fields ?? {})]),
    ...Object.values(config.startnewConfig?.attributeMapping ?? {}),
  ];
  return refs.some((ref) => ref?.startsWith(marker));
}
